import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestHeaders,
  AxiosResponse,
  InternalAxiosRequestConfig,
  RawAxiosRequestHeaders,
} from 'axios'
import { store } from '../store'
import { appFetch } from '../store/reducer/app'
import { toast } from 'react-toastify'
import { showErrorMessage } from '../utility/custom-error-response'

const { REACT_APP_BACKEND_API_URL, REACT_APP_LOCAL_API_URL } = process.env

export interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  showToastError?: boolean
  showToastSuccess?: boolean
}

const request: AxiosInstance = axios.create({
  baseURL: process.env.NODE_ENV === 'development' ? REACT_APP_LOCAL_API_URL : REACT_APP_BACKEND_API_URL,
})

const onRequest = (config: CustomAxiosRequestConfig): CustomAxiosRequestConfig => {
  if (config.url !== '/auth/token/') {
    const {
      app: { jwt },
    } = store.getState()
    config.headers = {
      ...(config.headers as RawAxiosRequestHeaders),
      Authorization: `token ${jwt}`,
    } as AxiosRequestHeaders
  }
  return config
}

const onRequestError = (error: AxiosError): Promise<AxiosError> => {
  console.error({ ...error })
  return Promise.reject({ ...error })
}

const onResponse = (response: AxiosResponse): AxiosResponse => {
  const {
    data: { message },
  } = response
  if ((response.config as CustomAxiosRequestConfig).showToastSuccess !== false) {
    toast(message, {
      type: 'success',
      position: 'top-center',
      style: {
        wordBreak: 'break-word',
        whiteSpace: 'pre-line',
        maxWidth: 400,
      },
    })
  }
  return response
}

let customOnResponseError: ((error: AxiosError) => Promise<any>) | null = null

const setCustomOnResponseError = (handler: (error: AxiosError) => Promise<any>) => {
  customOnResponseError = handler
}

const resetCustomOnResponseError = () => {
  customOnResponseError = null
}

const onResponseError = async (error: AxiosError): Promise<any> => {
  if (customOnResponseError) {
    await customOnResponseError(error)
    resetCustomOnResponseError()
    return Promise.reject(error)
  }

  const { response }: AxiosError = { ...error }
  if (response && response.data) {
    const { details, error_code, title } = response.data as { details?: any; error_code?: string; title?: string }
    const { status } = response

    if (status === 401 || status === 403) {
      if (error_code === 'ER-401-2') {
        await store.dispatch(appFetch())
      }
    } else if (error_code || title || details) {
      if ((response.config as CustomAxiosRequestConfig).showToastError !== false) {
        showErrorMessage(details, title)
      }
    }
  }
  console.error({ ...error })
  return Promise.reject({ ...error })
}

const createInterceptors = (axiosInstance: AxiosInstance): AxiosInstance => {
  axiosInstance.interceptors.request.use(onRequest, onRequestError)
  axiosInstance.interceptors.response.use(onResponse, onResponseError)
  return axiosInstance
}

createInterceptors(request)

export { request, setCustomOnResponseError, resetCustomOnResponseError }
